import { apiAxios, HostResourceResponse } from "api"
import { useEffect, useMemo, useState } from "react"
import { isMeetingRoom, TIME_SLOTS } from "../PortFacilitiesInnerPage/constants"
import {
  filterTimeSlotsToRange,
  getShouldDisableDate,
} from "../PortMeetingRoomProduct/useHostResource"

type HostSearchFilters = {
  location?: string
  type?: string
  capacity?: number
  startTime?: number
  endTime?: number
  date?: string
}

export function useHostSearch(filters: HostSearchFilters | null) {
  const [results, setResults] = useState<HostResourceResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)

  const filterKey = filters ? JSON.stringify(filters) : ""

  useEffect(() => {
    if (!filters) {
      setResults([])
      setSearched(false)
      return
    }

    let cancelled = false
    setLoading(true)

    // Build query params, omitting undefined/zero/empty values so the
    // backend doesn't receive misleading defaults (e.g. end_time=0).
    const params: Record<string, string | number> = {}
    if (filters.location) params.location = filters.location
    if (filters.type) params.type = filters.type
    if (filters.capacity) params.capacity = filters.capacity
    if (filters.startTime) params.start_time = filters.startTime
    if (filters.endTime) params.end_time = filters.endTime
    if (filters.date) params.date = filters.date

    apiAxios
      .get<{ body: HostResourceResponse[] }>(`/web/host/resources/search`, { params })
      .then((res) => {
        if (!cancelled && res.data?.body) {
          setResults(res.data.body)
          setSearched(true)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResults([])
          setSearched(true)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey])

  return { results, loading, searched }
}

/**
 * Build HOST search params from our SearchFilters.
 * Returns null if no HOST-relevant filters are active.
 */
export function buildHostSearchParams(
  filters: {
    location: string[]
    facilityType: string[]
    date: string
    startTime: string
    endTime: string
    numberOfPeople: string[]
  },
  hostLocationMap: Record<string, string>,
): HostSearchFilters | null {
  // Only call HOST when date or time is selected
  if (!filters.date && !filters.startTime) return null

  const params: HostSearchFilters = {}

  if (filters.location.length > 0) {
    const hostLoc = hostLocationMap[filters.location[0]]
    if (hostLoc) params.location = hostLoc
  }

  if (filters.facilityType.length > 0) {
    params.type = isMeetingRoom(filters.facilityType[0]) ? "Meeting Room" : undefined
  }

  if (filters.numberOfPeople.length > 0) {
    const num = parseInt(filters.numberOfPeople[0], 10)
    if (!isNaN(num)) params.capacity = num
  }

  if (filters.date) params.date = filters.date

  if (filters.startTime) {
    const [h] = filters.startTime.split(":").map(Number)
    params.startTime = h
  }

  if (filters.endTime) {
    const [h] = filters.endTime.split(":").map(Number)
    params.endTime = h
  }

  return params
}

export type HostAvailability = {
  // eslint-disable-next-line no-unused-vars
  shouldDisableDate?: (date: any) => boolean
  disabledTimeSlots: Set<string>
}

/**
 * Pre-fetches all HOST meeting room resources (no date/time filter)
 * and computes aggregate availability for the lobby date/time pickers.
 *
 * - shouldDisableDate: disables days where NO room is available
 *   (union of all rooms' availableDays)
 * - startTimeSlots / endTimeSlots: filtered to the widest working
 *   hours range across all rooms (min availableFrom → max availableTo)
 */
export function useHostAvailability(
  hasMeetingRooms: boolean,
  cmsResourceIds?: number[],
): HostAvailability {
  const [resources, setResources] = useState<HostResourceResponse[]>([])

  useEffect(() => {
    if (!hasMeetingRooms) {
      setResources([])
      return
    }

    let cancelled = false
    apiAxios
      .get<{ body: HostResourceResponse[] }>(`/web/host/resources/search`, {
        params: { type: "Meeting Room" },
      })
      .then((res) => {
        if (!cancelled && res.data?.body) {
          let filtered = res.data.body
          // Only consider resources that are mapped in the CMS
          if (cmsResourceIds && cmsResourceIds.length > 0) {
            const idSet = new Set(cmsResourceIds)
            filtered = filtered.filter((r) => idSet.has(r.id))
          }
          setResources(filtered)
        }
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMeetingRooms])

  return useMemo(() => {
    if (resources.length === 0) {
      return { disabledTimeSlots: new Set<string>() }
    }

    // Union of all rooms' availableDays
    const allDays = new Set<string>()
    let minFrom: number | undefined
    let maxTo: number | undefined

    for (const r of resources) {
      r.availableDays?.forEach((d) => allDays.add(d))
      if (r.availableFrom !== undefined) {
        minFrom = minFrom === undefined ? r.availableFrom : Math.min(minFrom, r.availableFrom)
      }
      if (r.availableTo !== undefined) {
        maxTo = maxTo === undefined ? r.availableTo : Math.max(maxTo, r.availableTo)
      }
    }

    const shouldDisableDate = getShouldDisableDate(
      allDays.size > 0 ? Array.from(allDays) : undefined,
    )

    // Compute which time slots fall outside the aggregate working hours
    const availableSlots = new Set(filterTimeSlotsToRange(TIME_SLOTS, minFrom, maxTo))
    const disabledTimeSlots = new Set<string>()
    for (const slot of TIME_SLOTS) {
      if (!availableSlots.has(slot)) disabledTimeSlots.add(slot)
    }

    return {
      shouldDisableDate,
      disabledTimeSlots,
    }
  }, [resources])
}
